import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCheck } = FiIcons;

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 1, delay, ease: [0.21, 0.47, 0.32, 0.98] }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function ClarityGuide() {
  useEffect(() => {
    // Inject the GoHighLevel form script
    const script = document.createElement('script');
    script.src = "https://link.nordiccrm.ca/js/form_embed.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const benefits = [
    "The 5 Honest Mirrors",
    "How to identify your true flow states",
    "Why burnout is often a symptom of misalignment",
    "How to reconnect your work with your deeper purpose",
    "One practical next step you can take immediately"
  ];

  return (
    <div className="w-full bg-kin-black min-h-screen">
      {/* ---------------------------------------------------------------------- */}
      {/* MAIN CONVERSION SECTION */}
      {/* ---------------------------------------------------------------------- */}
      <section className="relative pt-32 md:pt-48 pb-24 px-6 overflow-hidden">
        {/* Cinematic Background Elements */}
        <div className="absolute top-0 right-0 w-[60vw] h-[60vh] bg-kin-gold/5 blur-[140px] rounded-full pointer-events-none"></div>
        <div className="absolute bottom-0 left-0 w-[40vw] h-[40vh] bg-kin-gold/5 blur-[120px] rounded-full pointer-events-none"></div>
        
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            
            {/* LEFT COLUMN: CONTENT */}
            <div className="flex flex-col space-y-10">
              <FadeUp>
                <p className="text-kin-gold tracking-[0.4em] text-xs uppercase font-light mb-6"> Free Downloadable Guide </p>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif text-kin-ivory leading-[1.1] mb-8">
                  Get Instant Access To The Free <br />
                  <span className="italic text-kin-gold">Calling vs Career Clarity Guide</span>
                </h1>
                <p className="text-xl md:text-2xl text-kin-ivory/60 font-light leading-relaxed tracking-wide">
                  Discover the five reflective questions designed to help you identify the gap between external success and internal alignment.
                </p>
              </FadeUp>

              <FadeUp delay={0.2}>
                <div className="bg-[#09090b]/50 border border-kin-ivory/5 p-8 md:p-10 backdrop-blur-sm">
                  <h2 className="text-kin-ivory font-serif text-xl mb-8">Inside this free guide you will learn:</h2>
                  <ul className="space-y-5">
                    {benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-4 text-kin-ivory/60 font-light text-lg">
                        <SafeIcon icon={FiCheck} className="text-kin-gold w-5 h-5 mt-1 shrink-0" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </FadeUp>

              <FadeUp delay={0.4} className="pt-6">
                <div className="relative pl-10 border-l border-kin-gold/30">
                  <p className="text-2xl md:text-3xl font-serif text-kin-ivory italic leading-relaxed mb-4">
                    "You were not designed to choose between who you are and what you do."
                  </p>
                  <p className="text-kin-gold text-xs uppercase tracking-[0.3em] font-semibold">
                    — Dorman Baltazar
                  </p>
                </div>
              </FadeUp>
            </div>

            {/* RIGHT COLUMN: FORM */}
            <div className="relative">
              <FadeUp delay={0.3} className="w-full">
                <div className="bg-[#09090b] border border-kin-ivory/10 p-8 md:p-12 shadow-2xl relative">
                  <div className="absolute -top-4 -right-4 w-24 h-24 border-t border-r border-kin-gold/20 -z-10"></div>
                  
                  <div className="mb-10 text-center lg:text-left">
                    <h3 className="text-kin-ivory font-serif text-2xl mb-4">
                      Enter your details below and we'll send the guide directly to your inbox.
                    </h3>
                    <p className="text-kin-gold/70 text-sm italic">
                      Your guide will be delivered instantly to the email address entered above.
                    </p>
                  </div>

                  {/* GoHighLevel Form Embed */}
                  <div className="ghl-form-container w-full overflow-hidden rounded-md min-h-[450px]">
                    <iframe
                      src="https://link.nordiccrm.ca/widget/form/BJira9gRawDVWFtKsnLx"
                      style={{ width: "100%", height: "100%", border: "none", borderRadius: "8px" }}
                      id="inline-BJira9gRawDVWFtKsnLx"
                      data-layout="{'id':'INLINE'}"
                      data-trigger-type="alwaysShow"
                      data-trigger-value=""
                      data-activation-type="alwaysActivated"
                      data-activation-value=""
                      data-deactivation-type="neverDeactivate"
                      data-deactivation-value=""
                      data-form-name="Calling vs Career Clarity"
                      data-height="434"
                      data-layout-iframe-id="inline-BJira9gRawDVWFtKsnLx"
                      data-form-id="BJira9gRawDVWFtKsnLx"
                      title="Calling vs Career Clarity"
                    ></iframe>
                  </div>

                  <div className="mt-8 pt-8 border-t border-kin-ivory/5">
                    <p className="text-[11px] leading-relaxed text-kin-ivory/30 text-center uppercase tracking-widest font-light">
                      By submitting this form, you'll receive the Calling vs Career Clarity Guide and occasional updates from Kingdom Inspira Nova Studio. You may unsubscribe at any time.
                    </p>
                  </div>
                </div>
              </FadeUp>
            </div>

          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* PHILOSOPHY SECTION (MAINTAINED) */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-32 md:py-48 px-6 bg-[#09090b] border-y border-kin-ivory/5">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row gap-16 items-start">
          <div className="w-full md:w-1/2">
            <FadeUp>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif text-kin-ivory leading-tight mb-8"> 
                The Quiet Ache Most People Ignore 
              </h2>
            </FadeUp>
          </div>
          <div className="w-full md:w-1/2 space-y-8">
            <FadeUp delay={0.2}>
              <p className="text-kin-ivory/60 font-light leading-loose text-lg"> 
                Many people spend years building careers that look successful externally while internally feeling disconnected from themselves. 
              </p>
            </FadeUp>
            <FadeUp delay={0.3}>
              <p className="text-kin-gold uppercase tracking-[0.2em] text-xs font-semibold mb-6">The tension grows slowly:</p>
              <ul className="space-y-4">
                {['emotional fatigue','creative numbness','lack of fulfillment','constant overthinking','silent misalignment'].map((item,i)=> (
                  <li key={i} className="flex items-center gap-4 text-kin-ivory/80 font-light italic">
                    <span className="w-1.5 h-1.5 rounded-full bg-kin-gold/40"></span> {item}
                  </li>
                ))}
              </ul>
            </FadeUp>
          </div>
        </div>
      </section>

      {/* ---------------------------------------------------------------------- */}
      {/* FOOTER CALL TO ACTION */}
      {/* ---------------------------------------------------------------------- */}
      <section className="py-40 md:py-56 px-6 text-center relative overflow-hidden">
        <div className="max-w-4xl mx-auto relative z-10">
          <FadeUp>
            <h2 className="text-4xl md:text-6xl font-serif text-kin-ivory leading-tight mb-16"> Clarity Changes Everything. </h2>
            <p className="text-kin-ivory/40 text-sm tracking-[0.3em] uppercase">Scroll back up to download your copy</p>
          </FadeUp>
        </div>
      </section>
    </div>
  );
}